import { ExtranetTicketListDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetTab,
  ExtranetTabs,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NewTicketModal } from '../../components/contact/NewTicketModal'
import { TicketsTable } from '../../components/contact/TicketsTable'
import { ExtranetViewHeader } from '../../components/extranet/ExtranetViewHeader'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'

export type ContactViewProps = {
  activePage?: 'tickets' | 'closedTickets'
}

export const ContactView = (props: ContactViewProps) => {
  const { t } = useLanguage()
  const { minDesktop } = useBreakpoint()
  const navigate = useNavigate()
  const createPath = useExtranetPath()
  const [initialTabIndex, setInitialTabIndex] = useState<number>(() => {
    const isClosedTicketsPage = props.activePage && props.activePage === 'closedTickets'

    return isClosedTicketsPage ? 1 : 0
  })
  const [showModal, setShowModal] = useState<boolean>(false)
  const { customer } = useCustomerContext()

  useEffect(() => {
    const isClosedTicketsPage = props.activePage && props.activePage === 'closedTickets'
    setInitialTabIndex(isClosedTicketsPage ? 1 : 0)
  }, [props.activePage])

  const getOpenTickets = (customerId?: number): UseQueryOptions<ExtranetTicketListDto> => ({
    queryFn: async () => await api.tickets.getOpenTickets(customerId as number),
    queryKey: ['__getTickets', customerId, 'open'],
    enabled: typeof customerId === 'number',
    refetchInterval: 5 * 60 * 3000,
  })

  const getClosedTickets = (customerId?: number): UseQueryOptions<ExtranetTicketListDto> => ({
    queryFn: async () => await api.tickets.getClosedTickets(customerId as number),
    queryKey: ['__getTickets', customerId, 'closed'],
    enabled: typeof customerId === 'number',
    refetchInterval: 5 * 60 * 3000,
  })

  const { data: openTickets } = useQuery(getOpenTickets(customer?.id))
  const { data: closedTickets } = useQuery(getClosedTickets(customer?.id))

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        paddingMediumBottom
        paddingSmallHorizontal={!minDesktop}
      >
        <ExtranetViewHeader
          title={t('menu.contact')}
          children
        />
      </ExtranetContainer>
      {typeof initialTabIndex === 'number' && (
        <ExtranetTabs
          childrenAreAlwaysRendered
          selectedIndex={initialTabIndex}
          tabs={[
            {
              index: 0,
              handle: t('extranet:contact.openTicketsTabTitle'),
              element: (
                <ExtranetTab>
                  <ExtranetContainer>
                    <TicketsTable
                      tickets={openTickets?.data || []}
                      startNewConversation={() => setShowModal(true)}
                    />
                  </ExtranetContainer>
                </ExtranetTab>
              ),
              onClick: () => navigate(createPath('support.contact')),
            },
            {
              index: 1,
              handle: t('extranet:contact.closedTicketsTabTitle'),
              onClick: () => navigate(createPath('support.contact.closedTickets')),
              element: (
                <ExtranetTab>
                  <ExtranetContainer>
                    <TicketsTable
                      tickets={closedTickets?.data || []}
                      startNewConversation={() => setShowModal(true)}
                    />
                  </ExtranetContainer>
                </ExtranetTab>
              ),
            },
          ]}
        />
      )}
      {showModal && <NewTicketModal closeModal={() => setShowModal(false)} />}
    </ExtranetWrapper>
  )
}
